import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Image, View } from 'react-native';
import { PIPWidget, TWCText, UpcomingHourlyForecast } from '@components';
import { useRecoilValue } from 'recoil';

import { assets } from '@assets';
import { hourlyWeatherPageTestIDs } from '@constants';
import { helpers } from '@helpers';
import { useContextReference, useNodeHandle } from '@hooks';
import { screenReaderEnabledState } from '@store';

import { styles } from './styles';

import { VideoContext } from '../../../hooks/useIsVideoFocused';

const errorTPath = 'errors.';

import { HourlyWeatherMiddleRow } from '../HourlyWeatherMiddleRow/HourlyWeatherMiddleRow';
import { HourlyWeatherTopRow } from '../HourlyWeatherTopRow/HourlyWeatherTopRow';

type HourlyWeatherForecastPageContentProps = {
  error?: unknown;
};

export const HourlyWeatherForecastPageContent: FC<HourlyWeatherForecastPageContentProps> = ({ error }) => {
  const { t } = useTranslation();

  const topRowReference = useContextReference(hourlyWeatherPageTestIDs.hourlyWeatherTopRow);
  const middleRowReference = useContextReference(hourlyWeatherPageTestIDs.hourlyWeatherConditionsWidget);
  const bottomRowReference = useContextReference(hourlyWeatherPageTestIDs.hourlyWeatherTilesList);

  const screenReaderEnabled = useRecoilValue(screenReaderEnabledState);

  const topRowHandle = useNodeHandle(topRowReference);
  const middleRowHandle = useNodeHandle(middleRowReference);
  const bottomRowHandle = useNodeHandle(bottomRowReference);
  const bottomRowUpNode = screenReaderEnabled ? middleRowHandle : helpers.isAndroid ? bottomRowHandle : null;

  return (
    <View collapsable={false}>
      <PIPWidget
        height={160}
        pageID={hourlyWeatherPageTestIDs.hourlyWeatherPage}
        position={'topRight'}
        testID={hourlyWeatherPageTestIDs.pipWidget}
        videoContext={VideoContext.hourlyForecast}
        width={284}
      />
      <View style={styles.wrapper}>
        <HourlyWeatherTopRow defaultFocus={bottomRowReference} nextFocusDown={middleRowHandle} />
        <HourlyWeatherMiddleRow
          defaultFocus={bottomRowReference}
          nextFocusDown={bottomRowHandle}
          nextFocusUp={topRowHandle}
        />
        {error ? (
          <View style={styles.hourlyPageErrorWrapper}>
            <Image source={assets.sadCloud} style={styles.hourlyPageErrorImage} />
            <TWCText noTestID style={styles.hourlyPageErrorText} variant={'mediumText40'}>
              {t(`${errorTPath}noDataText`)}
            </TWCText>
          </View>
        ) : (
          <UpcomingHourlyForecast nextFocusUp={bottomRowUpNode} />
        )}
      </View>
    </View>
  );
};
